import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Calendar, Clock, Wrench, MapPin, User, ChevronRight, CheckCircle2, XCircle,
  Truck, ClipboardList, Plus, UserSearch, Boxes, ClipboardCheck, Cog, HelpCircle,
  Headphones, FileText, Package, Trophy, CalendarDays,
} from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/f681f627-76f3-48c3-81e7-2a2f48162459/images/ed3cbad4c70d3cbd00842cc5884d0f9a15dd4ba524aed9a73f420661ba4a15f0.jpeg";
const today = () => new Date().toISOString().slice(0, 10);
const STLABEL = { aberto: "ABERTO", atribuido: "ATRIBUÍDO", aceito: "AGENDADO", a_caminho: "A CAMINHO", concluido: "CONCLUÍDO", cancelado: "CANCELADO" };

export default function TechnicianOverview({ requests = [], available = [], userName, onStart, onNew, onNav, isAvailable = true, onToggleAvailability, onEnroute, onSupport }) {
  const m = useMemo(() => {
    const active = requests.filter((r) => !["concluido", "cancelado"].includes(r.status));
    const hoje = requests.filter((r) => r.scheduled_date === today() && r.status !== "cancelado");
    const aCaminho = requests.filter((r) => r.status === "a_caminho");
    const emAndamento = active.filter((r) => r.status !== "a_caminho");
    const agendados = requests.filter((r) => ["aceito", "atribuido"].includes(r.status) && r.scheduled_date);
    const finalizados = requests.filter((r) => r.status === "concluido");
    const cancelados = requests.filter((r) => r.status === "cancelado");
    const next = [...active].sort((a, b) =>
      `${a.scheduled_date || "9999"}${a.scheduled_time || "99"}`.localeCompare(`${b.scheduled_date || "9999"}${b.scheduled_time || "99"}`))[0];
    return { hoje, aCaminho, emAndamento, agendados, finalizados, cancelados, next };
  }, [requests]);

  const CountRow = ({ icon: Icon, n, label, cls }) => (
    <div className="flex items-center gap-3">
      <span className={`h-9 w-9 rounded-full flex items-center justify-center text-white flex-shrink-0 ${cls}`}><Icon className="h-4 w-4" /></span>
      <div><span className="font-bold text-slate-800">{n}</span> <span className="text-sm text-slate-500">{label}</span></div>
    </div>
  );

  const QUICK = [
    { icon: ClipboardList, label: "Meus chamados", fn: () => onNav("atendimentos") },
    { icon: Plus, label: "Abrir chamado", fn: onNew },
    { icon: UserSearch, label: "Consultar cliente", fn: onNew },
    { icon: Boxes, label: "Catálogo / Equip.", fn: () => onNav("equipamentos") },
    { icon: ClipboardCheck, label: "Check-list", fn: () => m.next && onStart(m.next) },
    { icon: Cog, label: "Peças e materiais", fn: () => m.next && onStart(m.next) },
    { icon: HelpCircle, label: "Balcão", fn: () => onNav("balcao") },
    { icon: Headphones, label: "Agenda", fn: () => onNav("agenda") },
  ];
  const FEATURES = [
    { icon: ClipboardCheck, title: "CHECK-LIST DIGITAL", desc: "Padronize os atendimentos e garanta qualidade.", fn: () => m.next && onStart(m.next) },
    { icon: FileText, title: "RELATÓRIO DE SERVIÇO", desc: "Registre fotos, observações e assine digitalmente.", fn: () => m.next && onStart(m.next) },
    { icon: Package, title: "CONTROLE DE PEÇAS", desc: "Consulte e registre peças utilizadas no serviço.", fn: () => onNav("equipamentos") },
    { icon: Headphones, title: "SUPORTE TÉCNICO", desc: "Fale com a central de suporte sempre que precisar.", fn: () => (onSupport ? onSupport() : onNav("agenda")) },
  ];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5" data-testid="technician-overview">
      <div className="xl:col-span-2 space-y-5">
        <div className="rounded-2xl bg-gradient-to-br from-[#0a2a6c] to-[#1e7be0] text-white p-5 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-extrabold">Olá, {userName?.split(" ")[0] || "Técnico"}!</h2>
            <p className="text-white/80 text-sm">Confira seus chamados e sua agenda de hoje.</p>
          </div>
          <button type="button" onClick={onToggleAvailability} data-testid="tech-availability-toggle"
            className="rounded-xl bg-white/95 text-slate-800 px-4 py-2 flex items-center gap-2 hover:bg-white transition-colors active:scale-[0.98]">
            <div className="text-right">
              <p className={`font-semibold ${isAvailable ? "text-emerald-600" : "text-amber-600"}`}>{isAvailable ? "Disponível" : "Ocupado"}</p>
              <p className="text-xs text-slate-500 flex items-center gap-1 justify-end">
                <span className={`h-2 w-2 rounded-full ${isAvailable ? "bg-emerald-500" : "bg-amber-500"}`} /> {isAvailable ? "Online" : "Sem novos chamados"}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        </div>

        {/* Agenda do dia */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800 text-sm">AGENDA DO DIA</h3>
            <button onClick={() => onNav("agenda")} className="text-sm text-[#1e7be0] hover:underline flex items-center gap-1">Ver agenda <ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-3 divide-x divide-slate-100 text-center">
            <div className="flex items-center justify-center gap-2"><Calendar className="h-7 w-7 text-[#1e7be0]" /><div className="text-left"><p className="text-2xl font-extrabold text-slate-800 leading-none">{m.hoje.length}</p><p className="text-xs text-slate-500">Chamados hoje</p></div></div>
            <div><p className="text-2xl font-extrabold text-slate-800">{m.emAndamento.length}</p><p className="text-xs text-slate-500">Em andamento</p></div>
            <div><p className="text-2xl font-extrabold text-slate-800">{m.aCaminho.length}</p><p className="text-xs text-slate-500">A caminho</p></div>
          </div>
        </div>

        {/* Próximo chamado */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold text-slate-800 text-sm mb-3">PRÓXIMO CHAMADO</h3>
          {m.next ? (
            <div className="space-y-2" data-testid="tech-next-request">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-800">#{m.next.ticket_number}</span>
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-[#1e7be0]">{STLABEL[m.next.status] || m.next.status}</span>
                </div>
                {m.next.scheduled_time && <span className="flex items-center gap-1 text-slate-600"><Clock className="h-4 w-4" /> {m.next.scheduled_time}</span>}
              </div>
              <p className="text-sm text-slate-700 flex items-center gap-2"><Wrench className="h-4 w-4 text-slate-400" /> {m.next.service_type} — {m.next.equipment}</p>
              <p className="text-sm text-slate-700 flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {m.next.service_address || "Endereço no chamado"}</p>
              <p className="text-sm text-slate-700 flex items-center gap-2"><User className="h-4 w-4 text-slate-400" /> Cliente: <b>{m.next.customer_name}</b></p>
              <div className="flex gap-2 mt-2">
                {["aceito", "atribuido"].includes(m.next.status) && (
                  <Button className="flex-1 bg-purple-600 hover:bg-purple-700 gap-1.5" onClick={() => onEnroute?.(m.next)} data-testid="tech-enroute-quick">
                    <Truck className="h-4 w-4" /> A caminho
                  </Button>
                )}
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={() => onStart(m.next)} data-testid="tech-start-service">
                  Iniciar atendimento <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : <p className="text-sm text-slate-400" data-testid="tech-next-empty">Nenhum chamado agendado no momento.</p>}
        </div>

        {/* Ações rápidas */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="font-semibold text-slate-800 text-sm mb-3">AÇÕES RÁPIDAS</h3>
          <div className="grid grid-cols-4 gap-3">
            {QUICK.map(({ icon: Icon, label, fn }) => (
              <button key={label} onClick={fn} className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-100 p-3 hover:border-[#1e7be0] hover:bg-blue-50/40 transition-colors" data-testid={`tech-quick-${label}`}>
                <Icon className="h-6 w-6 text-[#1e7be0]" />
                <span className="text-[11px] text-slate-600 text-center leading-tight">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Vantagens */}
        <div className="rounded-2xl overflow-hidden bg-[#0a2a6c] text-white flex items-stretch">
          <img src={HERO_IMG} alt="Técnico" className="w-40 object-cover hidden sm:block" />
          <div className="p-5">
            <p className="font-bold flex items-center gap-2 mb-2"><Trophy className="h-5 w-5 text-amber-300" /> VANTAGENS PARA O TÉCNICO</p>
            <ul className="space-y-1 text-sm text-white/90">
              {["Mais organização e produtividade", "Atendimentos otimizados", "Histórico sempre disponível", "Suporte rápido e eficiente"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Coluna direita */}
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 text-sm">MEUS CHAMADOS</h3>
            <button onClick={() => onNav("atendimentos")} className="text-sm text-[#1e7be0] hover:underline">Ver todos</button>
          </div>
          <div className="space-y-3">
            <CountRow icon={Wrench} n={m.emAndamento.length} label="Em andamento" cls="bg-[#1e7be0]" />
            <CountRow icon={Truck} n={m.aCaminho.length} label="A caminho" cls="bg-[#1e7be0]" />
            <CountRow icon={CalendarDays} n={m.agendados.length} label="Agendados" cls="bg-[#0a2a6c]" />
            <CountRow icon={CheckCircle2} n={m.finalizados.length} label="Finalizados" cls="bg-emerald-500" />
            <CountRow icon={XCircle} n={m.cancelados.length} label="Cancelados" cls="bg-red-500" />
          </div>
        </div>

        {FEATURES.map(({ icon: Icon, title, desc, fn }) => (
          <button key={title} onClick={fn} className="w-full text-left rounded-2xl bg-blue-50/60 border border-blue-100 p-4 flex items-center gap-3 hover:shadow-md transition-all" data-testid={`tech-feature-${title}`}>
            <span className="h-11 w-11 rounded-xl bg-[#1e7be0] text-white flex items-center justify-center flex-shrink-0"><Icon className="h-6 w-6" /></span>
            <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-800 text-sm">{title}</span><span className="block text-xs text-slate-500 leading-tight">{desc}</span></span>
            <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
